/**
 * 上架代理（LIST-3a）· 员工/管理员接口封装（不含代理令牌那组 `/agent/*`——那组只
 * 给代理程序调，admin-web 不碰）。真契约源 apps/api-server/src/api_server/routes/listing_jobs.py
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { api, apiConfig, ApiError } from "./api";
import type {
  AgentStatus, ListingAgent, ListingAgentWithToken, ListingJobDetail, ListingJobListRow,
  ListingJobProductMini, PreflightResult,
} from "./types/listingJobs";

const BASE = "/api/v1";

export function getAgentStatus(): Promise<AgentStatus> {
  return api<AgentStatus>(`${BASE}/listing-agents/status`);
}

/**
 * 「上架代理」这个 AI 功能是否开着——所有入口（产品列表按钮/产品详情入口行/渠道
 * 中心代理机块）共用同一个判断：员工都能调的 `/listing-agents/status` 404=关着，
 * 复用已有端点，不为「只读开关状态」这一个用途另开一个 `{enabled}` 端点。
 * 返回 `undefined` = 还不知道（首次加载中）——调用方按「先当作关着」处理，避免
 * 按钮闪现又消失。
 */
export function useListingAgentEnabled(): boolean | undefined {
  const q = useQuery({ queryKey: ["listing-agents", "status"], queryFn: getAgentStatus, retry: false });
  if (q.isSuccess) return true;
  if (q.isError && q.error instanceof ApiError && q.error.status === 404) return false;
  return undefined;
}

export function listListingJobs(params: { status?: string; platform?: string; product_id?: string } = {}): Promise<ListingJobListRow[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.platform) qs.set("platform", params.platform);
  if (params.product_id) qs.set("product_id", params.product_id);
  const suffix = qs.toString() ? `?${qs}` : "";
  return api<ListingJobListRow[]>(`${BASE}/listing-jobs${suffix}`);
}

export function getEligibleProducts(platform = "gyg"): Promise<ListingJobProductMini[]> {
  return api<ListingJobProductMini[]>(`${BASE}/listing-jobs:eligible-products?platform=${encodeURIComponent(platform)}`);
}

export function getPreflight(productId: string, platform = "gyg"): Promise<PreflightResult> {
  return api<PreflightResult>(`${BASE}/listing-jobs:preflight?product_id=${encodeURIComponent(productId)}&platform=${encodeURIComponent(platform)}`);
}

export function createListingJob(productId: string, platform = "gyg"): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs`, { method: "POST", body: JSON.stringify({ product_id: productId, platform }) });
}

export function getListingJob(id: string): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs/${id}`);
}

/** 截图是私有文件（要登录态 Authorization 头），不能直接当 <img src>——手动 fetch 转 objectURL。 */
export async function fetchStepScreenshotBlob(jobId: string, no: number): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${apiConfig.base}${BASE}/listing-jobs/${jobId}/steps/${no}/screenshot`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`截图加载失败（${res.status}）`);
  return res.blob();
}

export function confirmStep(jobId: string, no: number, choice?: number): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs/${jobId}/steps/${no}:confirm`, {
    method: "POST", body: JSON.stringify(choice == null ? {} : { choice }),
  });
}

export function redoFromStep(jobId: string, no: number): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs/${jobId}/steps/${no}:redo`, { method: "POST" });
}

export function markSubmitted(jobId: string): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs/${jobId}:mark-submitted`, {
    method: "POST", body: JSON.stringify({ confirm_copyright: true }),
  });
}

export function cancelListingJob(jobId: string): Promise<ListingJobDetail> {
  return api<ListingJobDetail>(`${BASE}/listing-jobs/${jobId}:cancel`, { method: "POST" });
}

// ─── ④ 渠道中心 · 代理机管理（owner/tech_admin）───────────────────────────────

export function listListingAgents(): Promise<ListingAgent[]> {
  return api<ListingAgent[]>(`${BASE}/listing-agents`);
}

export function createListingAgent(name: string): Promise<ListingAgentWithToken> {
  return api<ListingAgentWithToken>(`${BASE}/listing-agents`, { method: "POST", body: JSON.stringify({ name }) });
}

export function rotateListingAgentToken(id: string): Promise<ListingAgentWithToken> {
  return api<ListingAgentWithToken>(`${BASE}/listing-agents/${id}:rotate-token`, { method: "POST" });
}

export function revokeListingAgent(id: string): Promise<ListingAgent> {
  return api<ListingAgent>(`${BASE}/listing-agents/${id}:revoke`, { method: "POST" });
}
